import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { api, setToken } from "@/lib/api";
import { useAuth, homeForRole } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const ROLE_PRESETS = [
  { label: "Super Admin", email: "admin@sirodsecur.com", pw: "Admin@2026" },
  { label: "Supervisor", email: "sup1@sirodsecur.com", pw: "Super@2026" },
  { label: "Guard (mobile)", email: "guard1@sirodsecur.com", pw: "Guard@2026" },
  { label: "Client Admin", email: "client1@zenith.ng", pw: "Client@2026" },
];

export const Login = () => {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState("creds"); // creds | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [phoneHint, setPhoneHint] = useState("");
  const [loading, setLoading] = useState(false);

  const submitCreds = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.toLowerCase(), password });
      setChallengeId(data.challenge_id);
      setPhoneHint(data.phone_hint);
      setStep("otp");
      toast.success(`Demo OTP: ${data.otp_demo}`, { description: "Termii SMS mocked — code shown here for demo" });
      setOtp(data.otp_demo); // auto-fill for convenience in demo
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { challenge_id: challengeId, otp });
      setToken(data.access_token);
      setUser(data.user);
      toast.success(`Welcome, ${data.user.name}`);
      nav(homeForRole(data.user.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP failed");
    } finally { setLoading(false); }
  };

  const applyPreset = (p) => {
    setEmail(p.email);
    setPassword(p.pw);
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size={72} /></div>
        <div className="s-card" style={{ padding: 28 }}>
          {step === "creds"
            ? <CredsStep email={email} setEmail={setEmail} password={password} setPassword={setPassword}
                         loading={loading} onSubmit={submitCreds} onPreset={applyPreset} />
            : <OtpStep otp={otp} setOtp={setOtp} phoneHint={phoneHint} loading={loading}
                       onSubmit={submitOtp} onBack={() => setStep("creds")} />}
        </div>
      </div>
    </div>
  );
};

const CredsStep = ({ email, setEmail, password, setPassword, loading, onSubmit, onPreset }) => (
  <form onSubmit={onSubmit}>
    <div className="flex items-center gap-2 mb-1">
      <ShieldCheck size={18} color="#2ECAC8" />
      <h1 className="h-section">Sign in</h1>
    </div>
    <p className="text-muted" style={{ fontSize: 13, marginBottom: 24 }}>
      Two-factor required. We will text a 6-digit code.
    </p>

    <label className="text-muted" style={{ fontSize: 12 }}>Email</label>
    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
           className="s-input mt-1 mb-4" placeholder="you@example.com" data-testid="login-email" />

    <label className="text-muted" style={{ fontSize: 12 }}>Password</label>
    <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
           className="s-input mt-1 mb-6" placeholder="••••••••" data-testid="login-password" />

    <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit">
      {loading ? "Verifying..." : "Continue"}
    </button>

    <PresetGrid onPreset={onPreset} />
  </form>
);

const PresetGrid = ({ onPreset }) => (
  <div className="mt-6 pt-4" style={{ borderTop: "1px solid var(--border-dark)" }}>
    <div className="text-muted" style={{ fontSize: 11, marginBottom: 8 }}>QUICK DEMO ACCOUNTS</div>
    <div className="grid grid-cols-2 gap-2">
      {ROLE_PRESETS.map((p) => (
        <button key={p.label} type="button" onClick={() => onPreset(p)}
                className="text-left p-3 rounded-lg"
                style={{ background: "rgba(46,202,200,0.06)", border: "1px solid var(--border-dark)" }}
                data-testid={`preset-${p.label}`}>
          <div style={{ fontSize: 13, color: "#2ECAC8" }}>{p.label}</div>
          <div className="text-muted mono" style={{ fontSize: 11, marginTop: 2 }}>{p.email}</div>
        </button>
      ))}
    </div>
  </div>
);

const OtpStep = ({ otp, setOtp, phoneHint, loading, onSubmit, onBack }) => (
  <form onSubmit={onSubmit}>
    <h1 className="h-section mb-1">Enter OTP</h1>
    <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
      Sent to {phoneHint}. Demo code is pre-filled.
    </p>
    <input value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6}
           className="s-input mb-4 text-center mono" style={{ letterSpacing: 8, fontSize: 22 }}
           data-testid="login-otp" />
    <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-verify-otp">
      {loading ? "Signing in..." : "Verify and continue"}
    </button>
    <button type="button" onClick={onBack} className="w-full mt-3 text-muted" style={{ fontSize: 13 }}>
      Use a different account
    </button>
  </form>
);
